import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentRFQList = ({ rfqs = [] }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning/10 text-warning border-warning/20';
      case 'in_progress': return 'bg-primary/10 text-primary border-primary/20';
      case 'quoted': return 'bg-success/10 text-success border-success/20';
      case 'rejected': return 'bg-error/10 text-error border-error/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Recent RFQ Submissions</h2>
          <Link to="/rfq-processing">
            <Button variant="outline" size="sm">
              View All
            </Button>
          </Link>
        </div>
      </div>
      <div className="divide-y divide-border">
        {rfqs?.length > 0 ? (
          rfqs?.map((rfq) => (
            <div key={rfq?.id} className="p-6 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-medium text-foreground">RFQ #{rfq?.id}</h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(rfq?.status)}`}>
                      {rfq?.status?.replace('_', ' ')?.toUpperCase()}
                    </span>
                    <div className={`flex items-center space-x-1 ${getPriorityColor(rfq?.priority)}`}>
                      <Icon name="AlertCircle" size={14} />
                      <span className="text-xs font-medium capitalize">{rfq?.priority}</span>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{rfq?.title}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="User" size={12} />
                      <span>{rfq?.customerName}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Building" size={12} />
                      <span>{rfq?.company}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{formatTimeAgo(rfq?.submittedAt)}</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Link to={`/rfq-processing?rfq=${rfq?.id}`}>
                    <Button variant="outline" size="sm">
                      <Icon name="Eye" size={14} />
                      View
                    </Button>
                  </Link>
                  <Button variant="default" size="sm">
                    <Icon name="MessageSquare" size={14} />
                    Respond
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <span className="text-muted-foreground">
                    Category: <span className="text-foreground font-medium">{rfq?.category}</span>
                  </span>
                  <span className="text-muted-foreground">
                    Budget: <span className="text-foreground font-medium">{rfq?.budget}</span>
                  </span>
                </div>
                {rfq?.deadline && (
                  <span className="flex items-center space-x-1 text-warning">
                    <Icon name="Calendar" size={14} />
                    <span>Due: {new Date(rfq.deadline)?.toLocaleDateString()}</span>
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="p-12 text-center">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Recent RFQs</h3>
            <p className="text-muted-foreground">New RFQ submissions will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentRFQList;